// WeeklyRhythmCard — the centerpiece of the day on Home.
//
// Each day of the week carries its own theme (Mercy Monday → Sacrifice
// Sunday). The card pulls today's theme from /api/weekly-rhythm/today
// and — if the user is signed in — also pulls their 7-day streak +
// which days of THIS WEEK they've already marked complete.
//
// One tap on "Mark complete" advances the streak; the action button
// deep-links to the right page for the day's call to action.
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle, Flame, Play, Pause } from "@phosphor-icons/react";
import api from "../lib/api";
import { getActiveHdVoice } from "../lib/activeVoice";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import Tr from "./Tr";

const SHORT_DAYS = ["M", "T", "W", "T", "F", "S", "S"]; // Mon→Sun

export default function WeeklyRhythmCard() {
  const { user } = useAuth();
  const [theme, setTheme] = useState(null);
  const [me, setMe] = useState(null); // {streak, completed_today, completed_this_week, longest_streak}
  const [marking, setMarking] = useState(false);
  // Read-aloud state + audio element. We synthesize once per theme
  // change and cache the blob URL so repeat taps within the same
  // session are instant. Pauses on second tap; resets when the day
  // (theme.title) changes.
  const [reading, setReading] = useState(false);
  const audioRef = React.useRef(null);
  const readPassage = useMemo(() => {
    if (!theme) return "";
    return [
      `Today is ${theme.title}.`,
      theme.tagline || "",
      theme.verse_text ? `${theme.verse_text}. ${theme.verse_ref}.` : "",
      theme.prompt || "",
    ].filter(Boolean).join(" ");
  }, [theme]);
  // Cleanup audio on unmount / passage change.
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setReading(false);
    };
  }, [readPassage]);
  const toggleRead = async () => {
    // Pause if already playing this passage.
    if (audioRef.current && !audioRef.current.paused) {
      audioRef.current.pause();
      setReading(false);
      return;
    }
    // Resume if paused mid-read.
    if (audioRef.current && audioRef.current.paused && audioRef.current.currentTime > 0) {
      audioRef.current.play().catch(() => {});
      setReading(true);
      return;
    }
    // Fresh synthesis. We use the same endpoint as ReadAloudButton
    // for consistency (OpenAI TTS via backend, MP3 blob URL).
    try {
      setReading(true);
      const res = await api.post("/tts/speak", { text: readPassage, voice: getActiveHdVoice() }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = new Audio(url);
      audioRef.current = a;
      a.onended = () => setReading(false);
      a.onerror = () => setReading(false);
      await a.play();
    } catch (e) {
      setReading(false);
      toast.error("Could not read aloud. Try again.");
    }
  };

  useEffect(() => {
    // Pass the browser's tz_offset so the backend computes "today" in
    // the user's local time rather than UTC. Date.getTimezoneOffset()
    // returns minutes WEST of UTC (e.g. 240 for EDT, 300 for EST,
    // 420 for PDT). Without this, US users see Thursday's card all
    // Wednesday evening once UTC has rolled over.
    const tzOffset = new Date().getTimezoneOffset();
    api.get("/weekly-rhythm/today", { params: { tz_offset: tzOffset } })
       .then((r) => setTheme(r.data))
       .catch(() => {});
  }, []);

  useEffect(() => {
    if (!user) { setMe(null); return; }
    api.get("/weekly-rhythm/me").then((r) => setMe(r.data)).catch(() => setMe(null));
  }, [user]);

  const markComplete = async () => {
    if (!user) { toast.message("Sign in to track your streak"); return; }
    if (me?.completed_today) return;
    setMarking(true);
    try {
      const { data } = await api.post("/weekly-rhythm/complete");
      setMe(data);
      toast.success(`Streak: ${data.streak} day${data.streak === 1 ? "" : "s"} 🔥`);
    } catch {
      toast.error("Couldn't mark today complete");
    } finally {
      setMarking(false);
    }
  };

  if (!theme) return null;

  const todayIdx = theme.weekday; // Mon=0..Sun=6
  const doneSet = new Set(me?.completed_this_week || []);

  return (
    <section
      className="relative overflow-hidden rounded-3xl border"
      style={{ borderColor: `${theme.accent}33`, background: `linear-gradient(135deg, ${theme.accent_soft} 0%, #ffffff 80%)` }}
      data-testid="weekly-rhythm-card"
    >
      {/* Soft accent corner glow */}
      <div
        className="absolute -top-24 -right-24 w-72 h-72 rounded-full blur-3xl opacity-30 pointer-events-none"
        style={{ background: theme.accent }}
        aria-hidden="true"
      />

      <div className="relative p-6 md:p-8">
        {/* Day-of-week pill rail (Mon → Sun). Filled dots = completed,
            ring = today, hollow = upcoming. */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-1.5" data-testid="weekly-rhythm-week-dots">
            {SHORT_DAYS.map((d, i) => {
              const isToday = i === todayIdx;
              const done = doneSet.has(i);
              return (
                <div
                  key={`${d}-${i}`}
                  className="grid place-items-center text-[10px] font-semibold transition-all"
                  style={{
                    width: 26, height: 26, borderRadius: "50%",
                    background: done ? theme.accent : "transparent",
                    color: done ? "#fff" : (isToday ? theme.accent : "#9aa4b2"),
                    border: isToday ? `2px solid ${theme.accent}` : `1px solid ${done ? theme.accent : "#dfe3e8"}`,
                  }}
                  aria-label={done ? `${d} completed` : (isToday ? `Today (${d})` : d)}
                  data-testid={`weekly-rhythm-dot-${i}`}
                >
                  {done ? "✓" : d}
                </div>
              );
            })}
          </div>
          {me && me.streak > 0 && (
            <div
              className="inline-flex items-center gap-1 px-3 h-8 rounded-full text-xs font-semibold"
              style={{ background: theme.accent, color: "#fff" }}
              data-testid="weekly-rhythm-streak"
            >
              <Flame size={12} weight="fill" /> {me.streak} day streak
            </div>
          )}
        </div>

        {/* Theme title — Whole "Today is" block (label, headline,
            tagline, verse, and prompt) is wrapped as a single tap
            target that reads the entire passage aloud via the OpenAI
            TTS pipeline. Owner request (Feb 2026): "make the Today
            Is card read aloud — no more color pills, just words you
            tap to listen." Returns to a quiet 'Tap to listen / Now
            playing · tap to pause' inline affordance. */}
        <button
          type="button"
          onClick={toggleRead}
          data-testid="weekly-rhythm-read-aloud"
          className="block w-full text-left group focus:outline-none"
          aria-label={reading ? "Pause" : "Read aloud"}
        >
          <p
            className="text-[10px] tracking-[0.3em] uppercase font-bold opacity-90 inline-flex items-center gap-1.5"
            style={{ color: theme.accent }}
            data-testid="weekly-rhythm-day-label"
          >
            {reading ? <Pause size={10} weight="bold" /> : <Play size={10} weight="fill" className="group-hover:scale-125 transition-transform" />}
            {reading ? "Now playing · tap to pause" : "Today is — tap to listen"}
          </p>
          <h2
            className="font-heading text-3xl md:text-4xl mt-1.5 leading-tight"
            style={{ color: theme.accent }}
            data-testid="weekly-rhythm-title"
          >
            <span className="mr-2" aria-hidden="true">{theme.emoji}</span>
            <Tr>{theme.title}</Tr>
          </h2>
          <p className="text-ink/85 text-base md:text-lg italic mt-2 leading-snug"><Tr>{theme.tagline}</Tr></p>

          {/* Verse — inline so the tap-to-listen reads it too. */}
          <blockquote
            className="mt-5 pl-4 border-l-2"
            style={{ borderColor: theme.accent }}
            data-testid="weekly-rhythm-verse"
          >
            <p className="text-ink text-[15px] md:text-base leading-relaxed"><Tr>{theme.verse_text}</Tr></p>
            <p className="text-xs uppercase tracking-widest mt-2 font-semibold" style={{ color: theme.accent }}>
              {theme.verse_ref}
            </p>
          </blockquote>

          {/* Prompt — also read aloud. */}
          <p className="mt-5 text-sm md:text-[15px] text-ink leading-snug font-medium"><Tr>{theme.prompt}</Tr></p>
        </button>

        {/* Plain text-link actions (no more colored pills per owner
            request). Sit below the read-aloud surface so the audio
            tap target is clean. */}
        <div className="mt-5 flex flex-wrap items-center gap-5 text-sm">
          <Link
            to={theme.action_path}
            data-testid="weekly-rhythm-action"
            className="inline-flex items-center gap-1.5 font-semibold hover:underline underline-offset-4 transition-colors"
            style={{ color: theme.accent }}
          >
            <Tr>{theme.action_label}</Tr> <ArrowRight size={14} weight="bold" />
          </Link>
          <button
            onClick={markComplete}
            disabled={marking || me?.completed_today}
            data-testid="weekly-rhythm-mark-complete"
            className="inline-flex items-center gap-1.5 font-medium hover:underline underline-offset-4 transition-colors disabled:opacity-70 disabled:cursor-default"
            style={{ color: theme.accent }}
          >
            <CheckCircle size={14} weight={me?.completed_today ? "fill" : "regular"} />
            {marking ? "Saving…" : (me?.completed_today ? "Walked it today" : "I walked it today")}
          </button>
        </div>
      </div>
    </section>
  );
}
